import cx from 'classnames';

import { Content } from '../../../models/content/types';
import ContentCardPlaceholder from '../../../components/placeholders/content-card-placeholder';
import FeedHeader, { FeedHeaderProps, FeedViewAllFooter } from './feed-header';

import './content-layouts.scss';
import {
  FeedCardWithData,
  FeedCardProps,
  MobileListCard,
} from '../../content/solar-feed-card/solar-feed-card';
import {
  FeedCardGrid,
  FeedCardGridItem,
} from '@socialchorus/shared-ui-components';
import useScreenSize from '../../../common/use-screen-size';
import { MobileHorizontalFeedCards } from './horizontal-scroll-row';

interface IGridLayoutList {
  contents: Content[];
  limit: number;
  analyticsData: FeedCardProps['analyticsData'];
  title?: string;
  mobileWebLayouts: boolean;
  mobileWebParity: boolean;
}

const GridLayoutList = ({
  contents,
  limit,
  analyticsData,
  title,
  mobileWebLayouts,
  mobileWebParity,
}: IGridLayoutList) => {
  const { isMobile } = useScreenSize();

  if (isMobile && mobileWebLayouts) {
    return (
      <div className="content-layout__mobile-gutter">
        <FeedCardGrid columns={1} gap="small">
          {contents.slice(0, limit).map((content) => (
            <FeedCardGridItem key={content.id}>
              <MobileListCard
                content={content}
                analyticsData={analyticsData}
                titleHeadingLevel={2}
              />
            </FeedCardGridItem>
          ))}
        </FeedCardGrid>
      </div>
    );
  }

  if (isMobile && mobileWebParity) {
    return (
      <MobileHorizontalFeedCards
        contents={contents}
        analyticsData={analyticsData}
        title={title}
      />
    );
  }

  return (
    <FeedCardGrid columns={isMobile ? 1 : 2}>
      {contents.slice(0, limit).map((content) => (
        <FeedCardGridItem key={content.id}>
          <FeedCardWithData
            content={content}
            analyticsData={analyticsData}
            compact
            fullHeight
            layout="horizontal"
            titleHeadingLevel={2}
          />
        </FeedCardGridItem>
      ))}
    </FeedCardGrid>
  );
};

type ContentLayoutGridProps = FeedHeaderProps & {
  description?: string;
  viewAllText: string;
  feedUrl: string;
  contents: any;
  minLength: number;
  maxLength: number;
  isFetching: boolean;
  isTopic: boolean;
  /** The caller decides whether mobile-web treatment applies here — it also
   * owns the container markup those layouts depend on, which this component
   * has no visibility into. */
  mobileWebLayouts: boolean;
  mobileWebParity: boolean;
};

const ContentLayoutGrid = ({
  id,
  title,
  description,
  viewAllText,
  viewAllContext,
  feedUrl,
  contents,
  minLength,
  maxLength,
  isFetching,
  analyticsData,
  isTopic = false,
  mobileWebLayouts,
  mobileWebParity,
}: ContentLayoutGridProps) => {
  const { isMobile } = useScreenSize();
  const isMobileModule = isMobile && !!(mobileWebLayouts || mobileWebParity);

  const className = cx('home-filtered-feed', 'grid-layout-bkgd', {
    'home-filtered-feed--loading': isFetching,
  });

  if (!isFetching && contents.length < minLength) return null;

  return (
    <div className={className}>
      <FeedHeader
        id={id}
        title={title}
        description={description}
        viewAllLink={feedUrl}
        viewAllText={viewAllText}
        viewAllContext={viewAllContext}
        isTopic={isTopic}
        analyticsData={analyticsData}
        isMobileModule={isMobileModule}
      />
      {isFetching ? (
        <div
          data-testid="content-placeholder-list"
          className={cx('content-list', 'content-list--landscape', {
            'content-layout__mobile-gutter': isMobile && !!mobileWebLayouts,
          })}
        >
          {[...Array(maxLength)].map((_, idx) => (
            <ContentCardPlaceholder key={idx} />
          ))}
        </div>
      ) : (
        <GridLayoutList
          contents={contents}
          limit={maxLength}
          analyticsData={analyticsData}
          title={title}
          mobileWebLayouts={mobileWebLayouts}
          mobileWebParity={mobileWebParity}
        />
      )}
      {isMobileModule && feedUrl && (
        <FeedViewAllFooter
          viewAllLink={feedUrl}
          viewAllText={viewAllText}
          viewAllContext={viewAllContext}
          isTopic={isTopic}
          analyticsData={analyticsData}
        />
      )}
    </div>
  );
};

export default ContentLayoutGrid;
